import { ActionIcon, Badge, Card, Group, Menu, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBuildingCommunity, IconCopy, IconDots, IconHome } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AI_STATUS, RobotListItem } from '@mygpt/app';
import DeleteModal from '@/components/Settings/deleteModal';
import { CloneBotModal, CloneProgressModal } from '@/components/CloneBot';
import DonateToEnterpriseModal from '@/components/Robot/DonateToEnterpriseModal';
import { useBotList } from '@/hooks/useBotList';
import { useGPTStore } from '@/store';
import { useAdminStore } from '@/store/admin';
import Highlight from '@/components/Highlight';
import TagBadge from '@/components/TagBadge';

import {
  SvgAgent,
  SvgCatalogAgent,
  SvgGeneralAgent,
  SvgHandleChat,
  SvgHandleConversations,
  SvgHandleDashboard,
  SvgHandleDatasets,
  SvgHandleDelete,
  SvgHandleEdit,
  SvgRobot,
} from './icon';
import { isAgentRobot } from '@mygpt/app/utils';

interface props {
  robot: RobotListItem;
  searchTerm?: string;
  onCloned?: () => void;
  onDonated?: () => void;
}
export default ({ robot, searchTerm, onCloned, onDonated }: props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteModalOpened, { close: deleteModalClose, open: deleteModalOpen }] = useDisclosure(false);
  const [cloneModalOpened, { close: cloneModalClose, open: cloneModalOpen }] = useDisclosure(false);
  const [progressModalOpened, { close: progressModalClose, open: progressModalOpen }] = useDisclosure(false);
  const [donateModalOpened, { close: donateModalClose, open: donateModalOpen }] = useDisclosure(false);
  const { isSourceEmpty } = useBotList(robot);
  const { roles, assetScope } = useGPTStore();

  const isBotCloning = robot.ai_status === 'cloning_target' || robot.ai_status === 'cloning_source';
  const isBotCloneFailed = robot.ai_status === 'cloning_failed';

  const RobotName = robot.name || '---';

  const isCloning = useMemo(() => {
    return !!(robot.datasets || []).some((item) => ['cloning_target', 'cloning_source'].includes(item.data_status));
  }, [robot]);

  // 检查是否为本地部署版且权限为ROLE_VIEWER
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const { companyId } = useAdminStore();
  // Cloud版有组织检测
  const isCloudMode = !isLocalMode;
  const hasOrganization = isCloudMode && !!companyId;

  const isViewerOnly = useMemo(() => {
    if (!isLocalMode) return false;
    return roles && Array.isArray(roles) && roles.length > 0 && roles.every((r: any) => r.code === 'ROLE_VIEWER');
  }, [isLocalMode, roles]);

  // 权限显示组件
  const PermissionBadge = () => {
    // 本地部署版：必须有permission_type才显示
    // Cloud版：没有permission_type时默认显示OWNER，有则按实际显示
    let permissionType = robot.permission_type;

    if (isLocalMode) {
      // 本地部署版：没有permission_type就不显示
      if (!permissionType) return null;
    } else {
      // Cloud版：没有permission_type默认为OWNER
      if (!permissionType) {
        permissionType = 'OWNER';
      }
    }

    const getPermissionColor = (permission: string) => {
      switch (permission) {
        case 'OWNER':
          return 'blue';
        case 'EDIT':
          return 'green';
        case 'VIEW':
          return 'gray';
        default:
          return 'gray';
      }
    };

    return (
      <Badge
        color={getPermissionColor(permissionType)}
        size="sm"
        variant="light"
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        {permissionType}
      </Badge>
    );
  };

  const navigateAnimat = (url: string) => {
    // 如果是 share 路径，在新标签页打开
    if (url.startsWith('/share/')) {
      window.open(url, '_blank');
      return;
    }

    if (document.startViewTransition) {
      document.startViewTransition(() => navigate(url));
    } else {
      navigate(url);
    }
  };

  // 格式化 robot type 显示文本：首字母大写，去掉下划线
  // type 中不包含 agent 的都显示为 Chatbot
  const formatRobotType = (type: string) => {
    if (!isAgentRobot(type)) {
      return 'Chatbot';
    }
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // 根据 robot_type 渲染对应的图标
  const renderRobotIcon = () => {
    switch (robot.robot_type) {
      case 'general_agent':
        return <SvgGeneralAgent />;
      case 'catalog_agent':
        return <SvgCatalogAgent />;
      case 'agent':
        return <SvgAgent />;
      default:
        return <SvgRobot />;
    }
  };

  const handleEdit = () => {
    navigateAnimat(`/bot/${robot.id}/settings`);
  };
  // Cloud 企业模式判断
  const isCloudEnterprise = isCloudMode && !!assetScope;

  const handleChat = () => {
    // 本地部署版且权限为ROLE_VIEWER时跳转到share页面
    if (isViewerOnly) {
      navigateAnimat(`/share/${robot.id}`);
    } else if (isLocalMode && robot.permission_type === 'VIEW') {
      // 本地部署版，VIEW权限跳转到share页面
      navigateAnimat(`/share/${robot.id}`);
    } else if (isCloudEnterprise && robot.permission_type === 'VIEW') {
      // Cloud企业模式下，VIEW权限跳转到web应用的分享页
      window.open(`${import.meta.env.VITE_BASE_HOST}/bot/${robot.id}`, '_blank');
    } else {
      navigateAnimat(`/bot/${robot.id}/chat`);
    }
  };
  const handleDatasets = () => {
    navigateAnimat(`/bot/${robot.id}/data-source`);
  };
  const handleDashboard = () => {
    navigateAnimat(`/bot/${robot.id}/dashboard`);
  };
  const handleConversations = () => {
    navigateAnimat(`/bot/${robot.id}/records`);
  };
  const handleDelete = () => {
    deleteModalOpen();
  };
  const handleDonate = () => {
    donateModalOpen();
  };
  const handleDonated = () => {
    if (onDonated) {
      onDonated();
    } else {
      window.location.reload();
    }
  };

  const isOwner = robot.permission_type === 'OWNER' || (!isLocalMode && !isCloudEnterprise && !robot.permission_type);
  const canDonate = isOwner && isCloudMode && hasOrganization && !assetScope;

  const MoreMenu = () => (
    <Menu position="bottom-end" shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          radius={5}
          color="gray.5"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <IconDots />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Menu.Item leftSection={<SvgHandleEdit />} onClick={handleEdit}>
          {t('edit')}
        </Menu.Item>
        {!isViewerOnly && (
          <Menu.Item leftSection={<IconHome size={14} />} onClick={() => navigateAnimat(`/bot/${robot.id}/homepage`)}>
            {t('homepage.configure')}
          </Menu.Item>
        )}
        <Menu.Item leftSection={<SvgHandleChat />} onClick={handleChat}>
          {t('chat')}
        </Menu.Item>
        <Menu.Item leftSection={<SvgHandleDatasets />} onClick={handleDatasets}>
          {t('menu.bot.data_sets')}
        </Menu.Item>
        <Menu.Item leftSection={<SvgHandleDashboard />} onClick={handleDashboard}>
          {t('menu.bot.dashboard')}
        </Menu.Item>
        <Menu.Item leftSection={<SvgHandleConversations />} onClick={handleConversations}>
          {t('menu.bot.records')}
        </Menu.Item>
        {isOwner && (
          <Menu.Item leftSection={<IconCopy size={14} />} onClick={cloneModalOpen}>
            {t('clone_bot.menu')}
          </Menu.Item>
        )}
        {canDonate && (
          <Menu.Item leftSection={<IconBuildingCommunity size={14} />} onClick={handleDonate}>
            {t('donate.menu_label')}
          </Menu.Item>
        )}
        {isOwner && (
          <Menu.Item leftSection={<SvgHandleDelete />} c="red.5" onClick={handleDelete}>
            {t('delete_btn')}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  const handleCardClick = () => {
    if (isBotCloning || isBotCloneFailed) {
      progressModalOpen();
      return;
    }
    handleChat();
  };

  return (
    <>
      <Card
        shadow="sm"
        className="border border-solid border-gray-100 cursor-pointer hover:shadow-xl transition-all duration-300"
        onClick={handleCardClick}
        style={{ position: 'relative' }}
      >
        <PermissionBadge />
        {(isBotCloning || isBotCloneFailed) && (
          <Badge
            color={isBotCloneFailed ? 'red' : 'orange'}
            size="sm"
            variant="filled"
            style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 10, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              progressModalOpen();
            }}
          >
            {isBotCloneFailed
              ? t('clone_bot.status_failed')
              : robot.ai_status === 'cloning_source'
              ? t('clone_bot.status_source')
              : t('clone_bot.status_cloning_simple')}
          </Badge>
        )}
        <Card.Section>
          <Stack h={240} justify="space-between" gap={0} style={{ position: 'relative' }}>
            <Stack className="relative grow overflow-hidden" gap={12} p="md">
              <Group gap={8}>
                {renderRobotIcon()}
                <Badge size="sm" variant="light" color="violet" radius="md" tt="none">
                  {formatRobotType(robot.robot_type)}
                </Badge>
              </Group>

              <Text lineClamp={2} lh={'22px'}>
                <Highlight text={RobotName} searchTerm={searchTerm || ''} />
              </Text>
              <Text size="sm" c={'gray.5'}>
                {dayjs(robot.created_at).format('YYYY-MM-DD')}
              </Text>
              <Text size="sm" c={'gray.5'} lineClamp={3} className="text-wrap" style={{ lineHeight: '1' }}>
                <Highlight text={robot.discrible || ''} searchTerm={searchTerm || ''} />
              </Text>
              {robot.file_count != null && robot.file_count > 0 && robot.ai_status === AI_STATUS.INIT ? (
                <Group gap={5}>
                  <Text size="xs" fw={900} variant="gradient" gradient={{ from: 'grape', to: 'violet', deg: 90 }}>
                    {/* {t('learning')} */}
                  </Text>
                  {/* <Loader size="xs" type="dots" /> */}
                </Group>
              ) : robot.file_count != null && robot.faq_count != null && isSourceEmpty ? (
                <Text size={'xs'} fw={900} variant="gradient" gradient={{ from: 'grape', to: 'blue', deg: 90 }}>
                  {t('awaiting_sources')}
                </Text>
              ) : (
                <></>
              )}
              {/* Display tags if available */}
              {robot.tags && robot.tags.length > 0 && (
                <Group gap={4} wrap="wrap">
                  {robot.tags.slice(0, 3).map((tag) => (
                    <TagBadge key={tag.id} name={tag.name} color={tag.color} size="xs" />
                  ))}
                  {robot.tags.length > 3 && (
                    <Text size="xs" c="gray.5">
                      +{robot.tags.length - 3}
                    </Text>
                  )}
                </Group>
              )}
            </Stack>
            <Group justify="flex-end" py={5} px={15}>
              {robot.permission_type !== 'VIEW' && <MoreMenu />}
            </Group>
          </Stack>
        </Card.Section>
      </Card>
      <DeleteModal
        id={robot.id}
        name={RobotName}
        opened={deleteModalOpened}
        onClose={deleteModalClose}
        title={t('embeddings.chatbot.delete.title')}
        centered
        isCloning={isCloning}
      />
      <CloneBotModal
        opened={cloneModalOpened}
        onClose={cloneModalClose}
        aiId={robot.id}
        onCloned={() => {
          onCloned?.();
        }}
      />
      <CloneProgressModal opened={progressModalOpened} onClose={progressModalClose} aiId={robot.id} />
      {donateModalOpened && (
        <DonateToEnterpriseModal
          opened={donateModalOpened}
          onClose={donateModalClose}
          botId={robot.id}
          onDonated={handleDonated}
        />
      )}
    </>
  );
};
